import "./operations.css";
import { useEffect, useState, useContext, useCallback } from "react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import InputSelect from "../InputField/InputSelect/inputSelect";
import getCurrentDateTime from "../../utils/getCurrentDateTime";
import FormBuilderCartContext from "../../FormBuilder/cart/formBuilderCartContext";
import CsvDownloader from 'react-csv-downloader';
import { getCdeDetails } from "../../../api/searchApi";
import { cde_columns } from "../../utils/columns";
import { getDocUrl } from "../CrfDocLink/docUrl";
import sanitizeCsv from "../../utils/sanitizeCsv";

export default function Operations({
  operations,
  selectedRows,
  reportType,
  resetSelectedRows,
  alwaysEnabled,
  notificationsContext: { pushNotification, clearNotifications, }
}) {
  const [disableState, setDisableState] = useState(!alwaysEnabled);
  const [selectedOperation, setSelectedOperation] = useState('choose');
  const [dlProcessing, setDlProcessing] = useState(false);

  const { addCartItems, removeCartItems, clearCartItems, newCartItems, removeNewCartItems } = useContext(FormBuilderCartContext);

  useEffect(() => {
    if (!alwaysEnabled)
      setDisableState(selectedRows.length === 0);
  }, [selectedRows, alwaysEnabled]);

  const onClick = async () => {
    clearNotifications();

    switch (selectedOperation) {
      case 'choose':
        pushNotification({
          type: 'error',
          msg: 'Please select an operation.'
        });

        break;

      case 'fb_add':
        if (reportType === 'CRF') {
          const emptyCdeCrfs = selectedRows.filter(r => !r.cdeIds.length);

          if (emptyCdeCrfs.length)
            pushNotification({
              type: 'warning',
              msg: 'The following CRFs have no CDEs on the website and could not be added to form builder. Please download the archived materials.',
              list: emptyCdeCrfs.map(cc => cc.crfName).filter((val, idx, array) => array.indexOf(val) === idx)
            });

          // filter out CRFs with no associated CDEs
          selectedRows = selectedRows.filter(r => r.cdeIds.length);
          // map CRFs to CDEs
          const ids = selectedRows.flatMap(r => r.cdeIds.map(cdeId => `${cdeId}_${r.crfId}_${r.diseaseId}`));

          addCartItems(...ids);

          if (ids.length > 0)
            pushNotification({
              type: 'success',
              msg: `Performed 'Add selected to Form Builder' on ${ids.length} items.`
            });
        } else if (reportType === 'CDE') {
          const ids = selectedRows.map(r => `${r.cdeId}_${r.crfId}_${r.diseaseId}`);

          addCartItems(...ids);

          if (ids.length > 0)
            pushNotification({
              type: 'success',
              msg: `Performed 'Add selected to Form Builder' on ${ids.length} items.`
            });
        }

        resetSelectedRows();
        setSelectedOperation('choose');

        break;

      case 'csv':
        const zip = new JSZip();

        for (const row of selectedRows) {
          if (row.externalUrl && row.crfExtension !== '.noc') {
            try {
              const response = await fetch(getDocUrl(row.externalUrl));
              if (!response.ok) throw new Error("Network response was not ok");
              const blob = await response.blob();
              const fileName = row.externalUrl.split("/").pop();
              zip.file(fileName, blob);
            } catch (error) {
              console.error("Fetch error:", error);
            }
          }
        }

        if (!Object.keys(zip.files).length) {
          alert('No files available for download in current selection.');
          return;
        }

        const content = await zip.generateAsync({ type: "blob" });
        saveAs(content, `CRF_DATA_${getCurrentDateTime()}.zip`);

        resetSelectedRows();
        setSelectedOperation('choose');

        break;

      case 'cde':
        const uniqueDiseases = selectedRows.map(r => r.diseaseId).filter((val, idx, arr) => arr.indexOf(val) === idx).join(',');
        const uniqueCdeIds = selectedRows.map(r => r.id.split('_')[0]).filter((val, idx, arr) => arr.indexOf(val) === idx).join(',');
        const newRoute = `ninds/cde-details/${reportType}`;

        const handler = window.open(`${window.location.origin}/${newRoute}`);

        handler.onload = () => {
          handler.postMessage({message: 'cde-details', diseaseId: uniqueDiseases, crfcdeId: uniqueCdeIds});
        };

        break;

      case 'fb_undo':
        if (newCartItems.length) {
          removeNewCartItems();

          pushNotification({
            type: 'success',
            msg: 'Newly added CDEs removed from Form Builder cart.'
          });
        }
        else
          pushNotification({
            type: 'warning',
            msg: 'No new cart items to remove.'
          });

        resetSelectedRows();
        setSelectedOperation('choose');

        break;

      case 'fb_remove':
        if (!selectedRows.length) {
          pushNotification({
            type: 'warning',
            msg: 'No CDEs selected for removal.'
          });

          return;
        }

        const itemsToRemove = selectedRows.map(r => `${r.cdeId}_${r.crfId}_${r.diseaseId}`);

        removeCartItems(...itemsToRemove);

        pushNotification({
          type: 'success',
          msg: 'Selected CDEs removed from Form Builder cart.'
        });

        resetSelectedRows();
        setSelectedOperation('choose');

        break;

      case 'fb_clear':

        clearCartItems();

        pushNotification({
          type: 'success',
          msg: 'Cart cleared.'
        });

        resetSelectedRows();
        setSelectedOperation('choose');

        break;

      case 'fb_csv':
        //handled by prepDownload function
        break;

      default:
        break;
    }
  };

  const prepDownload = async () => {
    if (!selectedRows.length)
      return [];

    setDlProcessing(true);

    const uniqueDiseases = selectedRows.map(r => r.diseaseId).filter((val, idx, arr) => arr.indexOf(val) === idx);
    const uniqueIds = selectedRows.map(r => r.id.substring(0, r.id.lastIndexOf('_'))).filter((val, idx, arr) => arr.indexOf(val) === idx);

    // TODO: add new report type CDE_CART, unique ids will need to be cdeid_crfid_diseaseid
    const results = await getCdeDetails('CDE_CART', `${uniqueIds}`, `${uniqueDiseases}`);

    setDlProcessing(false);

    resetSelectedRows();
    setSelectedOperation('choose');

    return sanitizeCsv(results);
  };

  const handleEmpty = useCallback(() => {
    pushNotification({
      type: 'warning',
      msg: 'No CDEs selected for export.'
    });
  }, [pushNotification]);

  let executeButton = (
    <button type="button" className="btn-execute" disabled={disableState} onClick={onClick}>
      Execute
    </button>
  );

  if (selectedOperation === 'fb_csv')
    executeButton = (
      <CsvDownloader
        className="btn-execute"
        text={dlProcessing ? 'Processing...' : 'Execute'}
        disabled={dlProcessing}
        filename={`form-builder_${getCurrentDateTime()}`}
        datas={prepDownload}
        columns={cde_columns}
        wrapColumnChar={'"'}
        handleEmpty={handleEmpty}
      />
    );

  return (
    <div className={`operations ${disableState ? "disabled" : ""}`}>
      <InputSelect
        id="operations"
        className="operations-select"
        labelText="Operations"
        inputArr={operations}
        defaultVal="choose"
        selectedValue={selectedOperation}
        changeHandler={e => setSelectedOperation(e.target.value)}
      />
      {executeButton}
    </div>
  );
}
